import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Grid,
  Paper,
  Divider,
  Alert,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Dashboard as DashboardIcon,
  RecordVoiceOver as SuggestionIcon
} from '@mui/icons-material';
import { ProductSuggestionSettings as ProductSuggestionSettingsType } from '../../../types/storeSettings';
import { updateProductSuggestionSettings } from '../../../services/storeSettingsService';

interface ProductSuggestionSettingsProps {
  settings: ProductSuggestionSettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

const ProductSuggestionSettings: React.FC<ProductSuggestionSettingsProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const [formData, setFormData] = useState<ProductSuggestionSettingsType>(settings || {
    isEnabled: true,
    buttonText: 'أخبرنا إذا كان يباع بسعر أقل أو اقترح سعراً',
    destinationType: 'system',
    destinationValue: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onSave(true);
    onError(null);

    try {
      await updateProductSuggestionSettings(formData);
      onSuccess(true);
    } catch (err) {
      console.error('Error updating product suggestion settings:', err);
      onError('حدث خطأ أثناء حفظ الإعدادات');
    } finally {
      onSave(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SuggestionIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">إعدادات اقتراحات الأسعار (أخبرنا بسعر أقل)</Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isEnabled}
                onChange={(e) => setFormData({ ...formData, isEnabled: e.target.checked })}
              />
            }
            label={formData.isEnabled ? 'مفعل' : 'معطل'}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="نص الزر في صفحة المنتج"
              value={formData.buttonText}
              onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
              disabled={!formData.isEnabled}
              helperText="النص الذي يظهر للعميل (مثال: هل وجدت سعر أقل؟)"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl component="fieldset" disabled={!formData.isEnabled}>
              <FormLabel component="legend" sx={{ mb: 2 }}>مكان استقبال الرسائل</FormLabel>
              <RadioGroup
                value={formData.destinationType}
                onChange={(e) => setFormData({ ...formData, destinationType: e.target.value as any })}
              >
                <FormControlLabel 
                  value="system" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DashboardIcon sx={{ mr: 1, fontSize: 20 }} />
                      تلقي الرسالة داخل لوحة التحكم (النظام)
                    </Box>
                  } 
                />
                <FormControlLabel 
                  value="whatsapp" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <WhatsAppIcon sx={{ mr: 1, fontSize: 20, color: 'success.main' }} />
                      إرسال الرسالة عبر الواتساب
                    </Box>
                  } 
                />
                <FormControlLabel 
                  value="email" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EmailIcon sx={{ mr: 1, fontSize: 20, color: 'info.main' }} />
                      إرسال الرسالة عبر البريد الإلكتروني
                    </Box>
                  } 
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {(formData.destinationType === 'whatsapp' || formData.destinationType === 'email') && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={formData.destinationType === 'whatsapp' ? 'رقم الواتساب' : 'البريد الإلكتروني'}
                value={formData.destinationValue}
                onChange={(e) => setFormData({ ...formData, destinationValue: e.target.value })}
                placeholder={formData.destinationType === 'whatsapp' ? 'مثال: 201234567890' : 'مثال: admin@example.com'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.destinationType === 'whatsapp' ? <WhatsAppIcon color="success" /> : <EmailIcon color="info" />}
                    </InputAdornment>
                  ),
                }}
                disabled={!formData.isEnabled}
                helperText={formData.destinationType === 'whatsapp' ? 'أدخل الرقم مع مفتاح الدولة وبدون علامة +' : 'أدخل البريد الإلكتروني الذي ستصل عليه الاقتراحات'}
              />
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!formData.isEnabled}
          >
            حفظ إعدادات الاقتراحات
          </Button>
        </Box>
      </Paper>

      {formData.destinationType === 'system' && formData.isEnabled && (
        <Alert severity="info" sx={{ mt: 2 }}>
          سيتم استقبال جميع الاقتراحات في قسم "اقتراحات الأسعار" الجديد في لوحة التحكم، مع تفاصيل العميل والمنتج.
        </Alert>
      )}
    </Box>
  );
};

export default ProductSuggestionSettings;
